/**
 * ViewRenderer — renders any DrillView with the deterministic analytics
 * components. Single source of truth for BOTH the dashboard page and chat
 * view cards: identical rendering, identical react-query keys (the bridge
 * seeds those keys — see api/askChat.ts seedViewData; the keys ARE the
 * contract). `explain` toggles the per-view ExplainPanel (dashboard: on;
 * chat cards: off — the turn narration is the explanation there).
 */
import { useMemo, useState as orgUseState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  analyticsApi,
  parseEvidenceIds,
  type AnalyticsProblemEntryDto,
  type AttentionInboxItemDto,
} from '../../api/analytics';
import { clientRequestsApi, type ClientRequestDto } from '../../api/clientRequests';
import { reportsApi } from '../../api/reports';
import { invoicesApi } from '../../api/invoices';
import { journalApi } from '../../api/journal';
import { personsApi } from '../../api/persons';
import { getWorkspaceId } from '../../api/workspaces';
import MetricTile from './MetricTile';
import AgingBar from './AgingBar';
import DrillTable from './DrillTable';
import ProblemsPanel from './ProblemsPanel';
import SuggestionStrip from './SuggestionStrip';
import ExplainPanel from './ExplainPanel';
import { bucketField, todayStr, type DrillView } from './drillView';
import { CATEGORY_BADGE, CATEGORY_ORDER, SEVERITY_DOT } from './inboxShared';
import InboxItemCard from './InboxItemCard';
import { workItemsApi, type DispositionDto } from '../../api/workItems';
import { useToast } from '../Toast';
import { formatApiError } from '../../utils/errors';
import { formatMoney } from '../../utils/formatMoney';

export interface ViewRendererProps {
  view: DrillView;
  hostedReadOnly?: boolean; // mysql-synced book: Apskaita5 owns the writes
  onDrill: (view: DrillView) => void;
  aiEnabled?: boolean;
  explain?: boolean;
  overviewLoading?: boolean;
  overviewError?: string;
  onOpenBanking?: () => void;
  onOpenVat?: () => void;
}

export default function ViewRenderer(props: ViewRendererProps) {
  const { view } = props;
  switch (view.kind) {
    case 'inbox': return <InboxView {...props} />;
    case 'findings': return <ReadOnlyWrap {...props}><FindingsView {...props} view={view} /></ReadOnlyWrap>;
    case 'overview': return <OverviewView {...props} />;
    case 'customers': return <CustomersView {...props} view={view} />;
    case 'customer': return <ReadOnlyWrap {...props}><CustomerView {...props} view={view} /></ReadOnlyWrap>;
    case 'invoice': return <ReadOnlyWrap {...props}><InvoiceView {...props} view={view} /></ReadOnlyWrap>;
    case 'journal': return <ReadOnlyWrap {...props}><JournalView {...props} view={view} /></ReadOnlyWrap>;
    case 'bank': return <BankView {...props} />;
    case 'vat': return <VatView {...props} />;
  }
}

// Hosted (mysql-synced) books are READ-ONLY by design — Apskaita5 owns the writes.
// The banner tells the accountant WHERE fixes happen instead of leaving dead ends.
function ReadOnlyWrap({ hostedReadOnly, children }: ViewRendererProps & { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <>
      {hostedReadOnly && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-500">
          <span className="mt-0.5">☁</span>
          <span>{t('analytics.hostedReadOnly')}</span>
        </div>
      )}
      {children}
    </>
  );
}

// ─── L0: Attention Inbox — the ONE question: kas reikalauja dėmesio ─────────
// Four Dėmesys tabs (frozen product structure): Darbai (actionable work),
// Laukiama kliento / Atvejai (honest empty states until their features exist),
// Atlikta automatiškai (what the system completed — evidence + undo).

type InboxTab = 'darbai' | 'waiting' | 'cases' | 'auto';

function InboxView({ onDrill, aiEnabled }: ViewRendererProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { data: inbox, isLoading } = useQuery({
    queryKey: ['analytics-inbox', getWorkspaceId()],
    queryFn: analyticsApi.inbox,
    staleTime: 60_000,
  });
  const [running, setRunning] = orgUseState(false);
  const [rulesOpen, setRulesOpen] = orgUseState(false);
  const [tab, setTab] = orgUseState<InboxTab>('darbai');

  // MatchRules — model C's per-person auto-match rules (undo surface)
  const { data: matchRules } = useQuery({
    queryKey: ['match-rules', getWorkspaceId()],
    queryFn: analyticsApi.listMatchRules,
    staleTime: 30_000,
    retry: false,
  });

  // Laukiama kliento — open client questions (live tab content). LAZY: fetched
  // only when the tab is opened — the inbox mount stays fetch-free (the ask
  // canvas tests assert offline drilling fires no requests at all).
  const { data: openRequests } = useQuery({
    queryKey: ['client-requests', 'Open', getWorkspaceId()],
    queryFn: () => clientRequestsApi.list('Open'),
    enabled: tab === 'waiting',
    staleTime: 30_000,
    retry: false,
  });

  // Manual dispositions (Atlikta tab + the done-today counter). Lazy like the rest.
  const { data: dispositions } = useQuery({
    queryKey: ['work-item-dispositions', getWorkspaceId()],
    queryFn: workItemsApi.dispositions,
    enabled: tab === 'auto',
    staleTime: 30_000,
    retry: false,
  });

  // Person names for the 🤖 rules popover — "#42" tells the accountant nothing.
  const { data: rulePersons } = useQuery({
    queryKey: ['persons', getWorkspaceId()],
    queryFn: () => personsApi.list(),
    enabled: rulesOpen,
    staleTime: 60_000,
    retry: false,
  });

  const disableRule = async (ruleId: number) => {
    try {
      await analyticsApi.deleteMatchRule(ruleId);
      await queryClient.invalidateQueries({ queryKey: ['match-rules'] });
      await queryClient.invalidateQueries({ queryKey: ['analytics-inbox'] });
    } catch (err) {
      addToast(formatApiError(err), 'error');
    }
  };

  const items = inbox?.items ?? [];
  const workItems = items.filter(i => !i.automated);
  const autoItems = items.filter(i => i.automated);

  // Progress you can feel: what the system + the accountant completed today.
  const doneToday =
    autoItems.filter(i => new Date(i.timestamp).toDateString() === new Date().toDateString()).length +
    (dispositions ?? []).filter(d => new Date(d.actedAtUtc).toDateString() === new Date().toDateString()).length;

  // Category chips describe the Darbai tab (the default view) — computed from
  // its own items so automated entries don't inflate the counts.
  const workCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const i of workItems) counts.set(i.category, (counts.get(i.category) ?? 0) + 1);
    return [...counts.entries()]
      .sort((a, b) => (CATEGORY_ORDER[a[0]] ?? 9) - (CATEGORY_ORDER[b[0]] ?? 9))
      .map(([category, count]) => ({ category, count }));
  }, [workItems]);

  const tabs: Array<{ key: InboxTab; count: number }> = [
    { key: 'darbai', count: workItems.length },
    { key: 'waiting', count: openRequests?.length ?? 0 },
    { key: 'cases', count: 0 },
    { key: 'auto', count: autoItems.length },
  ];

  const drillItem = (item: AttentionInboxItemDto) => {
    switch (item.drillKind) {
      case 'Invoice': if (item.invoiceId) onDrill({ kind: 'invoice', invoiceId: item.invoiceId }); break;
      case 'Journal': if (item.journalEntryId) onDrill({ kind: 'journal', journalEntryId: item.journalEntryId }); break;
      case 'Customer': if (item.personId) onDrill({ kind: 'customer', personId: item.personId }); break;
      default: onDrill({ kind: 'findings' });
    }
  };

  const renderItemRow = (item: AttentionInboxItemDto, i: number) => {
    const overdue = item.daysOverdue !== null && item.daysOverdue > 0;
    return (
      <button
        key={i}
        onClick={() => drillItem(item)}
        className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary-50/50 transition-colors group"
      >
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${SEVERITY_DOT[item.severity ?? ''] ?? 'bg-gray-300'}`} />
        <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full flex-shrink-0 ${CATEGORY_BADGE[item.category] ?? 'bg-gray-100 text-gray-600'}`}>
          {t(`analytics.inbox.cat.${item.category}`)}
        </span>
        <span className="flex-1 text-sm text-gray-800 truncate">{item.title}</span>
        {item.amount != null && (
          <span className={`text-sm font-medium tabular-nums ${overdue ? 'text-red-600' : 'text-gray-700'}`}>
            {formatMoney(item.amount)}
          </span>
        )}
        {item.dueDate && (
          <span className="text-xs text-gray-400 flex-shrink-0 tabular-nums">
            {item.daysOverdue != null && item.daysOverdue > 0
              ? t('analytics.inbox.overdueBy', { days: item.daysOverdue })
              : new Date(item.dueDate).toLocaleDateString('lt-LT')}
          </span>
        )}
        {/* ✕ stop auto-match for this person (undo the MatchRule) — automated items only */}
        {item.automated && item.personId && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              const rule = matchRules?.find(r => r.personId === item.personId);
              if (rule) disableRule(rule.id);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                const rule = matchRules?.find(r => r.personId === item.personId);
                if (rule) disableRule(rule.id);
              }
            }}
            className="text-gray-300 hover:text-red-500 px-1.5 py-0.5 text-xs flex-shrink-0 cursor-pointer"
            title={t('analytics.inbox.rules.stopAuto')}
          >
            ✕
          </span>
        )}
        <svg className="w-4 h-4 text-gray-300 group-hover:text-primary-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    );
  };

  const runCheckButton = (
    <button
      onClick={async () => {
        setRunning(true);
        try {
          await analyticsApi.runScanners();
          await queryClient.invalidateQueries({ queryKey: ['analytics-inbox'] });
        } catch (err) {
          addToast(formatApiError(err), 'error');
        } finally {
          setRunning(false);
        }
      }}
      disabled={running}
      className="mt-4 px-4 py-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-full hover:border-primary-400 disabled:opacity-50 transition-colors"
    >
      {running ? t('analytics.inbox.running') : t('analytics.inbox.runCheck')}
    </button>
  );

  const workEmpty = (
    <div className="py-16 text-center">
      <div className="mx-auto mb-4 flex items-center justify-center w-12 h-12 rounded-2xl bg-green-50 border border-green-100">
        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <p className="text-base font-medium text-gray-800">{t('analytics.inbox.empty')}</p>
      <p className="text-sm text-gray-400 mt-1">{t('analytics.inbox.emptyHint')}</p>
      {runCheckButton}
    </div>
  );

  // Stebėjimai — Info-tier notices the system noticed: real, but not work. Collapsed,
  // grey, never counted in the tab badges; each row dismissible ("Ne problema").
  const observations = inbox?.observations ?? [];
  const [obsOpen, setObsOpen] = orgUseState(false);
  const disposeObservation = async (item: AttentionInboxItemDto) => {
    if (!item.stableKey) return;
    try {
      await workItemsApi.dispose(item.agentName, item.stableKey, 'Declined', { title: item.title });
      await queryClient.invalidateQueries({ queryKey: ['analytics-inbox'] });
      await queryClient.invalidateQueries({ queryKey: ['work-item-dispositions'] });
    } catch (err) {
      addToast(formatApiError(err), 'error');
    }
  };
  const observationsSection = observations.length > 0 && (
    <div className="border-t border-gray-100 pt-2 mt-2">
      <button
        onClick={() => setObsOpen(prev => !prev)}
        className="w-full flex items-center gap-2 px-1 py-1.5 text-left text-xs font-medium text-gray-400 hover:text-gray-600"
      >
        <svg className={`w-3.5 h-3.5 transition-transform ${obsOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        {t('analytics.inbox.observations')} ({observations.length})
      </button>
      {obsOpen && (
        <div className="mt-1 space-y-0.5">
          {observations.map((item, i) => (
            <div key={`${item.agentName}-${item.stableKey ?? item.title}-${i}`}
                 className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-gray-50/60">
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${SEVERITY_DOT[item.severity ?? ''] ?? 'bg-gray-300'}`} />
              <button onClick={() => drillItem(item)}
                      className="flex-1 min-w-0 text-left text-xs text-gray-500 truncate hover:text-gray-700">
                {item.title}
              </button>
              {item.stableKey && (
                <button
                  onClick={() => disposeObservation(item)}
                  className="text-[11px] text-gray-400 hover:text-gray-600 px-1.5 py-0.5"
                  title={t('analytics.inbox.card.notProblem')}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header: title + category chips (Darbai content) */}
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold text-gray-900">{t('analytics.inbox.title')}</h2>
        {doneToday > 0 && (
          <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-green-50 text-green-700 border border-green-200">
            ✓ {t('analytics.inbox.doneToday', { count: doneToday })}
          </span>
        )}
        {inbox?.generatedAtUtc && (
          <span className="text-xs text-gray-400">
            {t('analytics.problems.lastRun')}: {new Date(inbox.generatedAtUtc).toLocaleString('lt-LT')}
          </span>
        )}
        <div className="flex-1" />
        {tab === 'darbai' && workCounts.map(c => (
          <span key={c.category} className={`px-2.5 py-1 text-xs font-medium rounded-full ${CATEGORY_BADGE[c.category] ?? 'bg-gray-100 text-gray-600'}`}>
            {t(`analytics.inbox.cat.${c.category}`)} · {c.count}
          </span>
        ))}
        {/* Match rules management (model C undo) */}
        <div className="relative">
          <button
            onClick={() => setRulesOpen(prev => !prev)}
            className="px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            title={t('analytics.inbox.rules.title')}
          >
            🤖 {matchRules?.length ?? 0}
          </button>
          {rulesOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setRulesOpen(false)} />
              <div className="absolute top-full right-0 mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-20 p-3">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">{t('analytics.inbox.rules.title')}</p>
                {(matchRules ?? []).length === 0 && (
                  <p className="text-xs text-gray-400">{t('analytics.inbox.rules.empty')}</p>
                )}
                {(matchRules ?? []).map(rule => (
                  <div key={rule.id} className="flex items-center gap-2 py-1.5 border-b border-gray-50 last:border-0">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-gray-800">{rulePersons?.find(p => p.id === rule.personId)?.name ?? `#${rule.personId}`}</span>
                      <span className="text-xs text-gray-400 ml-1">±{(rule.amountTolerance * 100).toFixed(0)}%</span>
                      <span className="text-xs text-gray-400 ml-1">· {t('analytics.inbox.rules.timesUsed', { count: rule.timesUsedAuto })}</span>
                    </div>
                    <button
                      onClick={() => disableRule(rule.id)}
                      className="text-xs text-red-500 hover:text-red-700 px-2 py-0.5 rounded hover:bg-red-50"
                    >
                      {t('analytics.inbox.rules.disable')}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* The four Dėmesys tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 overflow-x-auto" role="tablist" aria-label={t('analytics.inbox.title')}>
        {tabs.map(({ key, count }) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`px-3.5 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === key
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t(`analytics.inbox.tabs.${key}`)}
            <span className={`ml-1.5 text-xs tabular-nums ${tab === key ? 'text-primary-600' : 'text-gray-400'}`}>{count}</span>
          </button>
        ))}
      </div>

      {isLoading && <div className="py-16 text-center text-gray-400">{t('analytics.loading')}</div>}

      {!isLoading && tab === 'darbai' && (
        workItems.length === 0 ? (
          <div>
            {workEmpty}
            {observationsSection}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="space-y-1">{workItems.map((item, i) => (
              <InboxItemCard key={`${item.agentName}-${item.stableKey ?? item.title}-${i}`} item={item} onDrill={onDrill} aiEnabled={aiEnabled} />
            ))}</div>
            {observationsSection}
          </div>
        )
      )}

      {!isLoading && tab === 'auto' && (
        autoItems.length === 0 && (dispositions ?? []).length === 0 ? (
          <TabEmptyState title={t('analytics.inbox.tabsEmpty.auto')} hint={t('analytics.inbox.tabsEmpty.autoHint')} />
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="space-y-1">
              {autoItems.map(renderItemRow)}
              {(dispositions ?? []).map((d: DispositionDto) => (
                <div key={d.id} className="flex items-center gap-3 px-3 py-2.5 border-t border-gray-50">
                  <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full flex-shrink-0 ${
                    d.status === 'ManualDone' ? 'bg-green-100 text-green-700'
                      : d.status === 'Declined' ? 'bg-gray-100 text-gray-500'
                      : 'bg-amber-100 text-amber-700'}`}>
                    {t(`analytics.inbox.atlikta.${d.status}`)}
                  </span>
                  <span className="flex-1 text-sm text-gray-700 truncate">
                    {d.title ?? d.stableKey}
                    {!d.title && <span className="text-xs text-gray-300"> ({d.agentName})</span>}
                    {d.reason && <span className="text-xs text-gray-400"> — {d.reason}</span>}
                  </span>
                  <span className="text-xs text-gray-400 tabular-nums">
                    {new Date(d.actedAtUtc).toLocaleDateString('lt-LT')}
                  </span>
                  <button
                    onClick={async () => {
                      try {
                        await workItemsApi.dispose(d.agentName, d.stableKey, 'Restore');
                        await queryClient.invalidateQueries({ queryKey: ['analytics-inbox'] });
                        await queryClient.invalidateQueries({ queryKey: ['work-item-dispositions'] });
                      } catch (err) {
                        addToast(formatApiError(err), 'error');
                      }
                    }}
                    className="text-xs text-primary-600 hover:text-primary-800 font-medium px-2 py-1"
                  >
                    {t('analytics.inbox.atlikta.restore')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {!isLoading && tab === 'waiting' && <WaitingTab />}

      {!isLoading && tab === 'cases' && (
        <TabEmptyState title={t('analytics.inbox.tabsEmpty.cases')} hint={t('analytics.inbox.tabsEmpty.casesHint')} />
      )}
    </div>
  );
}

function TabEmptyState({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="py-14 text-center">
      <div className="mx-auto mb-4 flex items-center justify-center w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100">
        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h8M8 11h8M8 15h5" />
        </svg>
      </div>
      <p className="text-base font-medium text-gray-800">{title}</p>
      <p className="text-sm text-gray-400 mt-1 max-w-md mx-auto">{hint}</p>
    </div>
  );
}

// ─── Laukiama kliento — open client questions (live tab) ────────────────────

const KIND_BADGE: Record<string, string> = {
  Question: 'bg-sky-100 text-sky-700',
  MissingDocument: 'bg-amber-100 text-amber-700',
  Approval: 'bg-violet-100 text-violet-700',
};

function WaitingTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { data: requests, isLoading } = useQuery({
    queryKey: ['client-requests', 'Open', getWorkspaceId()],
    queryFn: () => clientRequestsApi.list('Open'),
    staleTime: 30_000,
    retry: false,
  });
  const [showForm, setShowForm] = orgUseState(false);
  const [kind, setKind] = orgUseState('Question');
  const [text, setText] = orgUseState('');
  const [dueDate, setDueDate] = orgUseState('');
  const [busy, setBusy] = orgUseState(false);
  const [answers, setAnswers] = orgUseState<Record<number, string>>({});

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['client-requests'] });
  };

  const create = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await clientRequestsApi.create({
        kind: kind as 'Question' | 'MissingDocument' | 'Approval',
        text: text.trim(),
        dueDate: dueDate || null,
      });
      setText('');
      setDueDate('');
      setShowForm(false);
      await refresh();
    } catch (err) {
      addToast(formatApiError(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) {
    return <div className="py-12 text-center text-gray-400">{t('analytics.loading')}</div>;
  }

  if ((requests ?? []).length === 0 && !showForm) {
    return (
      <div>
        <TabEmptyState title={t('analytics.inbox.tabsEmpty.waiting')} hint={t('analytics.inbox.tabsEmpty.waitingHint')} />
        <div className="text-center -mt-6 pb-6">
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-full hover:border-primary-400 transition-colors"
          >
            + {t('analytics.inbox.waiting.newQuestion')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {(requests ?? []).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          {(requests ?? []).map((r: ClientRequestDto) => (
            <div key={r.id} className="border border-gray-100 rounded-lg p-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${KIND_BADGE[r.kind] ?? 'bg-gray-100 text-gray-600'}`}>
                  {t(`analytics.inbox.waiting.kind.${r.kind}`)}
                </span>
                <span className="text-xs text-gray-400">
                  {t('analytics.inbox.waiting.asked')} {new Date(r.createdAtUtc).toLocaleDateString('lt-LT')}
                  {r.dueDate && ` · ${t('analytics.inbox.waiting.due')} ${new Date(r.dueDate).toLocaleDateString('lt-LT')}`}
                </span>
                <div className="flex-1" />
                {r.scannedDocumentId && (
                  <a
                    href="/documents"
                    className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                  >
                    {t('analytics.inbox.waiting.document')} #{r.scannedDocumentId} ↗
                  </a>
                )}
              </div>
              <p className="text-sm text-gray-800 mt-1.5">{r.text}</p>
              <div className="flex items-center gap-2 mt-2">
                <input
                  value={answers[r.id] ?? ''}
                  onChange={e => setAnswers(prev => ({ ...prev, [r.id]: e.target.value }))}
                  placeholder={t('analytics.inbox.waiting.answerPlaceholder')}
                  className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                />
                <button
                  onClick={async () => {
                    try {
                      await clientRequestsApi.answer(r.id, answers[r.id] ?? '');
                      await refresh();
                    } catch (err) {
                      addToast(formatApiError(err), 'error');
                    }
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                >
                  {t('analytics.inbox.waiting.saveAnswer')}
                </button>
                <button
                  onClick={async () => {
                    try {
                      await clientRequestsApi.cancel(r.id);
                      await refresh();
                    } catch (err) {
                      addToast(formatApiError(err), 'error');
                    }
                  }}
                  className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  {t('analytics.inbox.waiting.cancelRequest')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm ? (
        <div className="bg-white border border-primary-200 rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2">
            <select
              value={kind}
              onChange={e => setKind(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
            >
              <option value="Question">{t('analytics.inbox.waiting.kind.Question')}</option>
              <option value="MissingDocument">{t('analytics.inbox.waiting.kind.MissingDocument')}</option>
              <option value="Approval">{t('analytics.inbox.waiting.kind.Approval')}</option>
            </select>
            <input
              type="date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
              title={t('analytics.inbox.waiting.due')}
            />
          </div>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={t('analytics.inbox.waiting.textPlaceholder')}
            rows={2}
            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
          />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              {t('common.cancel')}
            </button>
            <button
              onClick={create}
              disabled={busy || !text.trim()}
              className="px-4 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40"
            >
              {busy ? t('common.saving') : t('analytics.inbox.waiting.ask')}
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-full hover:border-primary-400 transition-colors"
          >
            + {t('analytics.inbox.waiting.newQuestion')}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── L0: Overview ───────────────────────────────────────────────────────────

function OverviewView({ onDrill, aiEnabled, explain = true, overviewLoading, overviewError }: ViewRendererProps) {
  const { t } = useTranslation();
  const { data: overview } = useQuery({ queryKey: ['analytics-overview', getWorkspaceId()], queryFn: analyticsApi.overview, staleTime: 60_000 });

  if (overviewLoading && !overview) {
    return <div className="py-16 text-center text-gray-400">{t('analytics.loading')}</div>;
  }
  if (overviewError) {
    return <div className="py-8 text-center text-red-600">{overviewError}</div>;
  }
  if (!overview) return null;

  const overdueTone = (overview.receivables?.overdueSharePercent ?? 0) >= 50 ? 'critical' : (overview.receivables?.overdueTotal ?? 0) > 0 ? 'warning' : 'positive';

  return (
    <div className="space-y-6">
      {/* Tiles — fill the width (6 up on xl) */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <MetricTile
          label={t('analytics.tile.receivables')}
          value={formatMoney(overview.receivables.totalOutstanding)}
          sub={t('analytics.tile.debtors', { count: overview.receivables.personCount })}
          onClick={() => onDrill({ kind: 'customers', type: 'receivable' })}
        />
        <MetricTile
          label={t('analytics.tile.overdue')}
          value={formatMoney(overview.receivables.overdueTotal)}
          sub={t('analytics.tile.overdueShare', { value: formatMoney(overview.receivables.overdueSharePercent, false) + ' %' })}
          tone={overdueTone}
          onClick={() => onDrill({ kind: 'customers', type: 'receivable', bucketDaysFrom: 1 })}
        />
        <MetricTile
          label={t('analytics.tile.payables')}
          value={formatMoney(overview.payables.totalOutstanding)}
          sub={t('analytics.tile.debtors', { count: overview.payables.personCount })}
          onClick={() => onDrill({ kind: 'customers', type: 'payable' })}
        />
        <MetricTile
          label={t('analytics.tile.cash')}
          value={formatMoney(overview.cash.total)}
          sub={overview.cash.accounts.length > 0 ? String(overview.cash.accounts[0]!.accountNumber) : ''}
          onClick={() => onDrill({ kind: 'bank' })}
        />
        <MetricTile
          label={t('analytics.tile.vat')}
          value={formatMoney(overview.vat.netVatPayable)}
          sub={overview.vat.periodFrom}
          onClick={() => onDrill({ kind: 'vat' })}
        />
        <MetricTile
          label={t('analytics.tile.findings')}
          value={String(overview.problems.totalFindings)}
          sub={overview.problems.groups[0] ? `${overview.problems.groups[0].scanner} · ${overview.problems.groups[0].count}` : ''}
          tone={overview.problems.totalFindings > 0 ? 'warning' : 'positive'}
          onClick={() => onDrill({ kind: 'findings' })}
        />
      </div>

      {/* Wide row: receivables aging (dominant) + problems feed */}
      <div className="grid xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('analytics.aging.title')}</h3>
          <AgingBar
            buckets={overview.receivables.buckets}
            total={overview.receivables.totalOutstanding}
            onSelectBucket={daysFrom => onDrill({ kind: 'customers', type: 'receivable', bucketDaysFrom: daysFrom })}
          />
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <ProblemsPanel
            problems={overview.problems}
            onSelectScanner={scanner => onDrill({ kind: 'findings', scanner })}
            onSelectFinding={(finding: AnalyticsProblemEntryDto) => {
              const ids = parseEvidenceIds(finding.evidence);
              if (ids.personId) onDrill({ kind: 'customer', personId: ids.personId, personName: finding.scanner });
              else if (ids.invoiceId) onDrill({ kind: 'invoice', invoiceId: ids.invoiceId });
              else if (ids.journalEntryId) onDrill({ kind: 'journal', journalEntryId: ids.journalEntryId });
              else onDrill({ kind: 'findings', scanner: finding.scanner });
            }}
          />
        </div>
      </div>

      {/* Second row: top debtors + payables aging + cash accounts */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('analytics.topDebtors')}</h3>
          <DrillTable
            rows={overview.receivables.topDebtors as unknown as Array<Record<string, unknown>>}
            columns={[
              { key: 'person', header: t('common.name'), value: r => (r.person as { name: string }).name },
              { key: 'totalOutstanding', header: t('analytics.col.total'), format: 'money' },
              { key: 'bucketOver90', header: t('analytics.aging.over90'), format: 'money' },
            ]}
            onRowClick={row => onDrill({ kind: 'customer', personId: (row.person as { id: number }).id, personName: (row.person as { name: string }).name })}
            pageSize={10}
          />
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('analytics.aging.titlePayable')}</h3>
          <AgingBar
            buckets={overview.payables.buckets}
            total={overview.payables.totalOutstanding}
            onSelectBucket={daysFrom => onDrill({ kind: 'customers', type: 'payable', bucketDaysFrom: daysFrom })}
          />
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('analytics.tile.cash')}</h3>
          <DrillTable
            rows={(overview.cash.accounts ?? []) as unknown as Array<Record<string, unknown>>}
            columns={[
              { key: 'accountNumber', header: t('analytics.col.account') },
              { key: 'accountName', header: t('common.name') },
              { key: 'balance', header: t('analytics.col.balance'), format: 'money' },
            ]}
            pageSize={10}
          />
        </div>
      </div>

      <SuggestionStrip facts={overview.facts} onSelect={target => {
        if (target === 'customers') onDrill({ kind: 'customers', type: 'receivable' });
        else if (target === 'suppliers') onDrill({ kind: 'customers', type: 'payable' });
        else if (target === 'bank') onDrill({ kind: 'bank' });
        else if (target === 'vat') onDrill({ kind: 'vat' });
        else if (target === 'findings') onDrill({ kind: 'findings' });
      }} />

      {aiEnabled && explain && <ExplainPanel view="overview" />}
    </div>
  );
}

// ─── L1: Customers (receivable/payable drill) ──────────────────────────────

function CustomersView({ view, onDrill }: ViewRendererProps & { view: Extract<DrillView, { kind: 'customers' }> }) {
  const { t } = useTranslation();
  const { type, bucketDaysFrom, filters } = view;
  const { data, isLoading } = useQuery({
    queryKey: ['debt-aging', type, todayStr()],
    queryFn: () => reportsApi.debtAging({ asOf: todayStr(), type }),
    staleTime: 60_000,
  });

  const columns = useMemo(() => [
    { key: 'name', header: t('common.name'), value: (r: Record<string, unknown>) => (r.person as { name: string }).name },
    { key: 'code', header: t('common.code'), value: (r: Record<string, unknown>) => (r.person as { code: string }).code },
    { key: 'total', header: t('analytics.col.total'), format: 'money' as const },
    { key: 'current', header: t('analytics.aging.current'), format: 'money' as const },
    { key: 'b1', header: t('analytics.aging.b1'), format: 'money' as const },
    { key: 'b31', header: t('analytics.aging.b31'), format: 'money' as const },
    { key: 'b61', header: t('analytics.aging.b61'), format: 'money' as const },
    { key: 'b90', header: t('analytics.aging.over90'), format: 'money' as const },
  ], [t]);

  const rows = useMemo(() => {
    const list = (data?.personDetails ?? []).map(p => ({
      person: p.person,
      total: p.totalOutstanding,
      current: p.currentAmount,
      b1: p.bucket1to30,
      b31: p.bucket31to60,
      b61: p.bucket61to90,
      b90: p.bucketOver90,
    })) as Array<Record<string, unknown>>;
    if (bucketDaysFrom === undefined) return list;
    const field = bucketField(bucketDaysFrom);
    return list.filter(row => (row[field] as number) > 0.005);
  }, [data, bucketDaysFrom]);

  return (
    <div className="space-y-4">
      {data && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <AgingBar
            buckets={data.buckets}
            total={data.totalOutstanding}
            onSelectBucket={daysFrom => onDrill({ kind: 'customers', type, bucketDaysFrom: daysFrom })}
          />
        </div>
      )}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <DrillTable
          rows={rows}
          columns={columns}
          loading={isLoading}
          initialSort={{ key: 'total', dir: 'desc' }}
          initialFilters={filters}
          pageSize={25}
          onRowClick={row => onDrill({
            kind: 'customer',
            personId: (row.person as { id: number }).id,
            personName: (row.person as { name: string }).name,
          })}
        />
      </div>
    </div>
  );
}

// ─── L2: Customer detail ────────────────────────────────────────────────────

function CustomerView({ view, onDrill, explain = true }: ViewRendererProps & { view: Extract<DrillView, { kind: 'customer' }> }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { personId, personName } = view;
  const from5y = new Date(Date.now() - 5 * 365 * 864e5).toISOString().slice(0, 10);

  const { data: debt } = useQuery({
    queryKey: ['debt-report', personId],
    queryFn: () => analyticsApi.debtReport(personId, todayStr()),
    staleTime: 60_000,
  });
  const { data: invoices, isLoading: invoicesLoading } = useQuery({
    queryKey: ['customer-invoices', personId],
    queryFn: () => invoicesApi.list({ direction: 'Made', from: from5y, to: todayStr(), limit: 500 }),
    staleTime: 60_000,
  });
  const myInvoices = useMemo(
    () => (invoices ?? []).filter(inv => inv.personId === personId),
    [invoices, personId],
  );

  return (
    <div className="space-y-4">
      {/* Header: who + the FIX PATH — person-card defects (missing VAT/identity codes)
          are fixed on /persons; before this button the fix page was unlinked. */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">{personName}</h2>
        <button
          onClick={() => navigate(`/persons?edit=${personId}`)}
          className="px-3 py-1.5 text-xs font-medium text-primary-700 bg-white border border-primary-200 rounded-lg hover:border-primary-400"
        >
          {t('analytics.customer.editPerson')}
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label={t('analytics.customer.debt')} value={formatMoney(debt?.endBalance ?? 0)} tone={(debt?.endBalance ?? 0) > 0 ? 'warning' : 'positive'} />
        <MetricTile label={t('analytics.customer.turnoverDebit')} value={formatMoney(debt?.debitTurnover ?? 0)} />
        <MetricTile label={t('analytics.customer.turnoverCredit')} value={formatMoney(debt?.creditTurnover ?? 0)} />
        <MetricTile label={t('analytics.customer.invoices')} value={String(myInvoices.length)} />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('analytics.customer.invoicesList')}</h3>
        <DrillTable
          rows={myInvoices as unknown as Array<Record<string, unknown>>}
          columns={[
            { key: 'date', header: t('common.date'), format: 'date' },
            { key: 'documentNumber', header: t('analytics.col.docNo') },
            { key: 'content', header: t('analytics.col.content') },
            { key: 'totalSum', header: t('analytics.col.total'), format: 'money' },
          ]}
          loading={invoicesLoading}
          initialSort={{ key: 'date', dir: 'desc' }}
          pageSize={15}
          emptyText={t('analytics.customer.noInvoices')}
          onRowClick={row => onDrill({ kind: 'invoice', invoiceId: row.id as number })}
        />
      </div>

      {explain && <ExplainPanel view="customer" entityId={personId} title={personName} />}
    </div>
  );
}

// ─── L2: Invoice detail + possible payments ────────────────────────────────

/** Journal entry lines → table rows (shared by the InvoiceView inline panel + JournalView). */
function journalLineRows(entry: unknown): Array<Record<string, unknown>> {
  const lines = (entry as { lines?: Array<{ account: number; accountName: string; entryType: string; amount: number; personId: number | null }> } | null | undefined)?.lines ?? [];
  return lines.map(line => ({
    account: line.account,
    accountName: line.accountName,
    debit: line.entryType === 'Debetas' ? line.amount : 0,
    credit: line.entryType === 'Kreditas' ? line.amount : 0,
    personId: line.personId,
  }));
}

function InvoiceView({ view, explain = true, onDrill }: ViewRendererProps & { view: Extract<DrillView, { kind: 'invoice' }> }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [settling, setSettling] = orgUseState<number | null>(null); // bankJournalEntryId being confirmed
  const [settled, setSettled] = orgUseState<Set<number>>(new Set()); // bankJournalEntryIds already matched
  const [teachRule, setTeachRule] = orgUseState<number | null>(null); // personId to offer auto-match
  const { invoiceId } = view;
  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => invoicesApi.getById(invoiceId),
    retry: false,
  });
  // The journal entry is fetched ALWAYS (not just as the invoice-missing fallback):
  // every document IS a journal entry (same ID), and the invoice panel renders its
  // debit/credit lines inline — the "surinktų duomenų visuma" document view.
  const invoiceMissing = !isLoading && !invoice;
  const { data: journalFallback, isLoading: journalLoading } = useQuery({
    queryKey: ['journal-entry', invoiceId],
    queryFn: () => journalApi.getById(invoiceId),
    staleTime: 60_000,
  });
  const journalRows = useMemo(() => journalLineRows(journalFallback), [journalFallback]);
  const { data: matches, isLoading: matchesLoading } = useQuery({
    queryKey: ['matching-payments', invoiceId],
    queryFn: () => analyticsApi.findMatchingPayments(invoiceId),
    enabled: !!invoice,
    staleTime: 60_000,
  });

  const confirmSettle = async (bankJournalEntryId: number) => {
    setSettling(null);
    try {
      await analyticsApi.autoSettle(invoiceId, bankJournalEntryId);
      setSettled(prev => new Set(prev).add(bankJournalEntryId));
      // Model C: offer to teach this pattern — "next time, do it automatically?"
      const invoicePersonId = invoice?.personId;
      if (invoicePersonId && invoicePersonId > 0) {
        setTeachRule(invoicePersonId);
      }
      // Refresh the workspace's reconciliation state so the finding disappears
      await queryClient.invalidateQueries({ queryKey: ['matching-payments'] });
      await queryClient.invalidateQueries({ queryKey: ['analytics-inbox'] });
      await queryClient.invalidateQueries({ queryKey: ['analytics-overview'] });
    } catch (err) {
      // NEVER pretend success: an unreconciled payment that looks reconciled is the
      // worst failure mode this screen has. The confirmation card re-opens.
      addToast(t('analytics.invoice.settleFailed') + ' — ' + formatApiError(err), 'error');
      setSettling(null); // close the confirmation card so the failure is visible
      return;
    }
  };

  return (
    <div className="space-y-4">
      {/* Journal fallback: no invoices_made row but the journal entry exists */}
      {invoiceMissing && journalFallback && (
        <div>
          <p className="text-xs text-gray-400 mb-2">{t('analytics.invoice.journalFallback')}</p>
          <JournalView view={{ kind: 'journal', journalEntryId: invoiceId }} explain={explain} onDrill={onDrill} />
        </div>
      )}
      {invoiceMissing && journalLoading && (
        <div className="py-8 text-center text-gray-400">{t('analytics.loading')}</div>
      )}
      {invoiceMissing && !journalLoading && !journalFallback && (
        <div className="py-8 text-center text-gray-400">{t('analytics.invoice.notFound')}</div>
      )}
      {invoice && (
        <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-400">{t('analytics.invoice.doc')}</span>
          <button
            onClick={() => navigate(`/invoices/credit-note/new?linkedInvoiceId=${invoiceId}&direction=Made`)}
            className="px-3 py-1 text-[11px] font-medium text-primary-700 bg-white border border-primary-200 rounded-full hover:border-primary-400"
          >
            {t('invoices.createCreditNote')}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricTile label={t('analytics.col.docNo')} value={invoice.documentNumber} />
          <MetricTile label={t('common.date')} value={invoice.date?.slice(0, 10) ?? ''} />
          <MetricTile label={t('analytics.col.total')} value={formatMoney(invoice.totalSum)} />
          {onDrill && (
            <MetricTile
              label={t('analytics.journal.entryTile')}
              value="↗"
              onClick={() => onDrill({ kind: 'journal', journalEntryId: invoiceId })}
            />
          )}
        </div>
        </div>
      )}
      {isLoading && <div className="py-8 text-center text-gray-400">{t('analytics.loading')}</div>}

      {/* The document's own journal lines — everything about this record on one screen */}
      {invoice && !journalLoading && journalRows.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('analytics.journal.lines')}</h3>
          <p className="text-xs text-gray-400 mb-2">{(journalFallback as { content?: string } | undefined)?.content}</p>
          <DrillTable
            rows={journalRows}
            columns={[
              { key: 'account', header: t('analytics.col.account') },
              { key: 'accountName', header: t('common.name') },
              { key: 'debit', header: t('analytics.journal.debit'), format: 'money' },
              { key: 'credit', header: t('analytics.journal.credit'), format: 'money' },
            ]}
            pageSize={10}
            emptyText={t('analytics.journal.noLines')}
          />
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('analytics.invoice.matchesTitle')}</h3>
        {matchesLoading && <p className="text-sm text-gray-400 py-2">{t('analytics.table.loading')}</p>}
        {!matchesLoading && (!matches || matches.length === 0) && (
          <p className="text-sm text-gray-400 py-2">{t('analytics.invoice.noMatches')}</p>
        )}
        {matches && matches.length > 0 && (
          <div className="space-y-2">
            {(matches as unknown as Array<{
              journalEntryId: number; date: string; documentNumber: string;
              personName: string | null; content: string | null;
              amount: number; amountDifference: number;
            }>).filter(m => !settled.has(m.journalEntryId)).map((m, i) => (
              <div key={i} className="border border-gray-100 rounded-lg p-3 hover:border-primary-200 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-800 truncate">
                      {m.documentNumber} · {new Date(m.date).toLocaleDateString('lt-LT')} · {m.personName ?? '—'}
                    </div>
                    <div className="text-xs text-gray-400 truncate">{m.content}</div>
                  </div>
                  <span className="text-sm font-medium tabular-nums text-gray-900">{formatMoney(m.amount)}</span>
                  {Math.abs(m.amountDifference) > 0.005 && (
                    <span className="text-xs text-amber-600 tabular-nums">Δ {formatMoney(m.amountDifference)}</span>
                  )}
                  {onDrill && (
                    <button
                      onClick={() => onDrill({ kind: 'journal', journalEntryId: m.journalEntryId })}
                      className="text-xs text-gray-400 hover:text-primary-600 px-2 py-1"
                      title={t('analytics.journal.lines')}
                    >
                      {t('analytics.journal.entryTile')} ↗
                    </button>
                  )}
                  <button
                    onClick={() => setSettling(m.journalEntryId)}
                    className="px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-full hover:border-primary-400 transition-colors"
                  >
                    {t('analytics.invoice.settle')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Model C teach-rule toast: "next time, do it automatically for this client?" */}
        {teachRule !== null && (
          <div className="fixed bottom-6 right-6 z-40 max-w-sm bg-white rounded-xl shadow-xl border border-primary-200 p-4">
            <p className="text-sm font-medium text-gray-900 mb-1">{t('analytics.invoice.teachTitle')}</p>
            <p className="text-xs text-gray-500 mb-1">{t('analytics.invoice.teachBody')}</p>
            <p className="text-[10px] text-gray-400 mb-3">{t('analytics.inbox.rules.toastHint')}</p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setTeachRule(null)}
                className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                {t('common.no')}
              </button>
              <button
                onClick={async () => {
                  const personId = teachRule;
                  setTeachRule(null);
                  try {
                    await analyticsApi.createMatchRule(personId);
                    await queryClient.invalidateQueries({ queryKey: ['analytics-inbox'] });
                  } catch (err) {
                    addToast(formatApiError(err), 'error');
                  }
                }}
                className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
              >
                {t('common.yes')}
              </button>
            </div>
          </div>
        )}

        {/* Confirmation gate — the approve pattern from the copilot's pending-write flow */}
        {settling !== null && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/20 p-4">
            <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-5 max-w-sm w-full">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('analytics.invoice.settleConfirmTitle')}</h3>
              <p className="text-sm text-gray-600 mb-1">{t('analytics.invoice.settleConfirmBody')}</p>
              <p className="text-xs text-gray-400 mb-4">{t('analytics.invoice.settleConfirmHint')}</p>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setSettling(null)}
                  className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => confirmSettle(settling)}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                >
                  {t('analytics.invoice.settle')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {explain && <ExplainPanel view="invoice" entityId={invoiceId} />}
    </div>
  );
}

// ─── L3: Journal entry — the DEEPEST record (debit/credit lines) ────────────

function JournalView({ view }: ViewRendererProps & { view: Extract<DrillView, { kind: 'journal' }> }) {
  const { t } = useTranslation();
  const { journalEntryId } = view;
  const { data: entry, isLoading } = useQuery({
    queryKey: ['journal-entry', journalEntryId],
    queryFn: () => journalApi.getById(journalEntryId),
    staleTime: 60_000,
  });

  const rows = useMemo(() => journalLineRows(entry), [entry]);

  return (
    <div className="space-y-4">
      {entry && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <MetricTile label={t('analytics.col.docNo')} value={entry.documentNumber} />
          <MetricTile label={t('common.date')} value={entry.date?.slice(0, 10) ?? ''} />
          <MetricTile label={t('analytics.journal.total')} value={formatMoney(entry.totalDebit)} />
        </div>
      )}
      {isLoading && <div className="py-8 text-center text-gray-400">{t('analytics.loading')}</div>}

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('analytics.journal.lines')}</h3>
        <p className="text-xs text-gray-400 mb-2">{entry?.content}</p>
        <DrillTable
          rows={rows}
          columns={[
            { key: 'account', header: t('analytics.col.account') },
            { key: 'accountName', header: t('common.name') },
            { key: 'debit', header: t('analytics.journal.debit'), format: 'money' },
            { key: 'credit', header: t('analytics.journal.credit'), format: 'money' },
          ]}
          loading={isLoading}
          pageSize={25}
          emptyText={t('analytics.journal.noLines')}
        />
      </div>
    </div>
  );
}

// ─── L1: Findings (decision log / scanner results) ─────────────────────────

function FindingsView({ view, onDrill }: ViewRendererProps & { view: Extract<DrillView, { kind: 'findings' }> }) {
  const { t } = useTranslation();
  const { scanner } = view;
  const { data, isLoading } = useQuery({
    queryKey: ['findings', scanner ?? 'all'],
    queryFn: () => analyticsApi.findings({ take: 100 }),
    staleTime: 30_000,
  });

  // Same rank as the server's SeverityRank — Critical 3 > Error 2 > Warning 1 > rest 0.
  const severityRank = (s: unknown): number => {
    const v = String(s ?? '');
    if (v === 'Critical') return 3;
    if (v === 'Error') return 2;
    if (v === 'Warning') return 1;
    return 0;
  };

  const rows = useMemo(() => {
    let list = (data?.items ?? []).map(e => ({
      timestamp: e.timestamp,
      severity: e.severity ?? '',
      severityRank: severityRank(e.severity),
      scanner: e.agentName,
      title: e.outcome,
      evidence: e.evidence,
    }));
    if (scanner) list = list.filter(r => r.scanner.toLowerCase() === scanner.toLowerCase());
    return list as Array<Record<string, unknown>>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, scanner]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <DrillTable
        rows={rows}
        columns={[
          { key: 'severity', header: t('analytics.col.severity'), value: r => (r.severity ? String(r.severity) : '—') },
          { key: 'timestamp', header: t('analytics.col.time'), format: 'date' },
          { key: 'scanner', header: t('analytics.col.scanner') },
          { key: 'title', header: t('analytics.col.title') },
        ]}
        loading={isLoading}
        initialSort={{ key: 'severityRank', dir: 'desc' }}
        pageSize={25}
        onRowClick={row => {
          const ids = parseEvidenceIds(String(row.evidence ?? ''));
          if (ids.personId) onDrill({ kind: 'customer', personId: ids.personId });
          else if (ids.invoiceId) onDrill({ kind: 'invoice', invoiceId: ids.invoiceId });
          else if (ids.journalEntryId) onDrill({ kind: 'journal', journalEntryId: ids.journalEntryId });
        }}
      />
    </div>
  );
}

// ─── L1: Bank / VAT (shallow views over overview data) ──────────────────────

function BankView({ overviewLoading, onOpenBanking }: ViewRendererProps) {
  const { t } = useTranslation();
  const { data: overview } = useQuery({ queryKey: ['analytics-overview', getWorkspaceId()], queryFn: analyticsApi.overview, staleTime: 60_000 });

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">{t('analytics.tile.cash')}</h3>
        {onOpenBanking && (
          <button onClick={onOpenBanking} className="text-xs text-primary-600 hover:text-primary-800 font-medium">
            {t('analytics.bank.openFull')} →
          </button>
        )}
      </div>
      <DrillTable
        rows={(overview?.cash.accounts ?? []) as unknown as Array<Record<string, unknown>>}
        columns={[
          { key: 'accountNumber', header: t('analytics.col.account') },
          { key: 'accountName', header: t('common.name') },
          { key: 'balance', header: t('analytics.col.balance'), format: 'money' },
        ]}
        loading={overviewLoading}
        pageSize={10}
      />
    </div>
  );
}

function VatView({ onOpenVat }: ViewRendererProps) {
  const { t } = useTranslation();
  const { data: overview } = useQuery({ queryKey: ['analytics-overview', getWorkspaceId()], queryFn: analyticsApi.overview, staleTime: 60_000 });
  const vat = overview?.vat;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <MetricTile label={t('analytics.vat.salesVat')} value={formatMoney(vat?.salesVat ?? 0)} />
        <MetricTile label={t('analytics.vat.purchaseVat')} value={formatMoney(vat?.purchaseVat ?? 0)} />
        <MetricTile label={t('analytics.vat.net')} value={formatMoney(vat?.netVatPayable ?? 0)} tone={(vat?.netVatPayable ?? 0) > 0 ? 'warning' : 'positive'} />
      </div>
      {onOpenVat && (
        <button onClick={onOpenVat} className="text-sm text-primary-600 hover:text-primary-800 font-medium">
          {t('analytics.vat.openFull')} →
        </button>
      )}
    </div>
  );
}
